import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, CircularProgress, Divider, Link, Stack, StickyTooltip, Typography, screenSizeBigger} from 'views/components';
import {Check} from 'views/icons';
import {CommandResponseView, ConnectionBadge, HoverToolBox, Translate, useCommand} from 'views/module';
import DatePickerQuickOptionCalendar from '../../../../module/dates/DatePickerQuickOptionCalendar';
import CommandTextViewer from '../../../../module/task/components/CommandTextViewer';

import {getDueDateColour} from '../../utils';

import moment from 'moment';

const TaskItem = (props) => {
  const {
    task,
    onClickEditTask,
    onUpdateDueDate,
    removeTask,
    onCompleteTask,
    isHoverToolboxEnabled = true,
    isDueDatePickerEnabled = true,
    requester,
    skills,
    statusProgressWrap,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const maxDisplayedAvatarIcons = 5;
  const dueDateMoment = moment(task.dueDate);
  const isToday = dueDateMoment.isSame(moment(), 'day');
  const connections = task.connectionInfo || [];
  const ownerRns = Array.isArray(task.ownerRns) ? task.ownerRns :
    Array.isArray(task.ownerRn) ? task.ownerRn :
      task.ownerRn ? [task.ownerRn] : [];
  const owners = (task.ownerInfo || []).length > 0 ?
    task.ownerInfo :
    ownerRns
        .filter(Boolean)
        .map((ownerRn) => ({userRn: ownerRn, name: ownerRn}));
  const {clientConnections, nonClientConnections} = connections.reduce((acc, connection) => {
    const isClientConnection = /^RN:CLIENT-V1-[^:]+$/.test(connection.connectionRn);

    if (isClientConnection) {
      acc.clientConnections.push(connection);
    } else {
      acc.nonClientConnections.push(connection);
    }

    return acc;
  }, {clientConnections: [], nonClientConnections: []});
  const visibleClientConnection = clientConnections[0];
  const hiddenClientConnections = clientConnections.slice(1);
  const visibleNonClientConnections = nonClientConnections.slice(0, maxDisplayedAvatarIcons);
  const hiddenNonClientConnections = nonClientConnections.slice(maxDisplayedAvatarIcons);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isDueDatePickerOpen, setIsDueDatePickerOpen] = React.useState(false);
  const rightEdgeAnchorRef = React.useRef(null);
  const dueDateButtonRef = React.useRef(null);

  const skillTitle = React.useMemo(() => {
    const skill = skills.find((entry) => entry.skillRn === task.skillRn);
    return skill ? skill.name : null;
  }, [task, skills]);

  const command = useCommand({
    requester,
    skills,
    statusProgressWrap,
    task,
  });

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };
  const handleDueDateClick = (event) => {
    if (!isDueDatePickerEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isStatusComplete) {
      return;
    }

    setIsDueDatePickerOpen(true);
  };
  const closeDueDatePicker = () => {
    setIsDueDatePickerOpen(false);
  };
  const handleDueDateChange = (nextValue) => {
    if (isStatusComplete) {
      return;
    }

    const parsedValue = nextValue ? moment(nextValue) : null;
    const nextDueDateIso = parsedValue?.isValid?.() ? parsedValue.toISOString() : null;
    const currentDueDateIso = dueDateMoment.toISOString();

    if (nextDueDateIso === currentDueDateIso) {
      return;
    }

    if (onUpdateDueDate) {
      onUpdateDueDate(nextDueDateIso);
    }
  };

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  const handleApproveAndSendSuccess = async () => {
    if (onCompleteTask) {
      await onCompleteTask(task);
    }
  };

  return (
    <Box
      sx={{
        display: 'contents',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          minWidth: 0,
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        <Box
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isStatusComplete && onCompleteTask) {
              onCompleteTask(task);
            }
          }}
          sx={{
            'width': 20,
            'height': 20,
            'borderRadius': '50%',
            'border': '1.5px solid',
            'borderColor': isStatusComplete ? 'success.main' : 'divider',
            'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'flexShrink': 0,
            'cursor': isStatusComplete ? 'default' : 'pointer',
            '& .todo-check': {
              transition: 'color 120ms ease',
              color: isStatusComplete ? 'common.white' : 'transparent',
              fontSize: 10,
            },
            '&:hover': {
              borderColor: 'success.main',
            },
            '&:hover .todo-check': {
              color: isStatusComplete ? 'common.white' : 'success.main',
            },
          }}
        >
          <Check className="todo-check" />
        </Box>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: getDueDateColour(isStatusComplete, dueDateMoment),
            flexShrink: 0,
          }}
        />
        <Stack direction='row' alignItems='center' spacing={1} sx={{flex: 1, minWidth: 0}}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 400,
              minWidth: 0,
              textDecoration: isStatusComplete ? 'line-through' : 'none',
            }}
          >
            <CommandTextViewer
              value={task.title}
              owners={owners}
              connections={connections}
              type={task.type}
              skill={skillTitle}
            />
          </Typography>
          {
            command.canRunCommand &&
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {
                  command.isExecutingCommand &&
                    <CircularProgress
                      size={16}
                      sx={{
                        color: '#4B4843',
                      }}
                    />
                }
                {
                  !command.isExecutingCommand &&
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={command.runCommand}
                      sx={{
                        minWidth: '30px',
                        px: '12px',
                        py: '8px',
                        borderRadius: '7px',
                        borderColor: '#D8D4CE',
                        background: '#FCFCFC',
                        color: '#4B4843',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '100%',
                        textTransform: 'none',
                      }}
                    >
                      <Translate expression='tasks.Common.Run' />
                    </Button>
                }
              </Box>
          }
        </Stack>
      </Stack>
      <Box
        sx={{
          minWidth: 0,
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        {
          nonClientConnections.length > 0 &&
            <Stack
              direction="row"
              spacing={-.5}
              sx={{
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              {
                visibleNonClientConnections.map((connection, index) => (
                  <ConnectionBadge
                    key={connection.connectionRn}
                    connection={connection}
                    sx={{
                      width: '30px',
                      height: '30px',
                      position: 'relative',
                      zIndex: index + 1,
                      fontSize: '12px',
                      outline: '2px solid',
                      outlineColor: 'background.default',
                    }}
                  />
                ))
              }
              {
                hiddenNonClientConnections.length > 0 &&
                  <StickyTooltip
                    title={
                      <Stack spacing={0.5}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                          }}
                        >
                          {
                            hiddenNonClientConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')
                          }
                        </Typography>
                      </Stack>
                    }
                  >
                    <Box
                      sx={{
                        width: '30px',
                        height: '30px',
                        position: 'relative',
                        zIndex: visibleNonClientConnections.length + 1,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#E2DEDA',
                        color: 'text.secondary',
                        fontSize: '12px',
                        fontWeight: 600,
                        outline: '2px solid',
                        outlineColor: 'background.default',
                      }}
                    >
                      +{hiddenNonClientConnections.length}
                    </Box>
                  </StickyTooltip>
              }
            </Stack>
        }
      </Box>
      <Box
        sx={{
          minWidth: 0,
          py: 1.5,
          ml: -0.5,
          opacity: isStatusComplete ? 0.45 : 1,
          textAlign: 'right',
        }}
      >
        {
          visibleClientConnection &&
          <Stack
            direction='row'
            spacing={0.5}
            justifyContent='flex-end'
            alignItems='center'
            sx={{
              minWidth: 0,
            }}
          >
            <Link
              href={`/client/${visibleClientConnection.connectionRn}`}
              target='_blank'
              rel='noopener noreferrer'
              underline='none'
              sx={{
                'display': 'block',
                'fontSize': '0.875rem',
                'lineHeight': 1.43,
                'fontWeight': 400,
                'color': 'text.secondary',
                'overflow': 'hidden',
                'textOverflow': 'ellipsis',
                'whiteSpace': 'nowrap',
                'cursor': 'pointer',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              {getConnectionDisplayName(visibleClientConnection)}
            </Link>
            {
              hiddenClientConnections.length > 0 &&
              <StickyTooltip
                title={
                  <Stack spacing={0.5}>
                    {
                      hiddenClientConnections.map((clientConnection) => (
                        <Link
                          key={clientConnection.connectionRn}
                          href={`/client/${clientConnection.connectionRn}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          sx={{
                            'color': 'inherit',
                            'fontSize': '12px',
                            'lineHeight': 1.4,
                            '&:hover': {
                              color: 'primary.light',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {getConnectionDisplayName(clientConnection)}
                        </Link>
                      ))
                    }
                  </Stack>
                }
              >
                <Typography
                  variant='body2'
                  sx={{
                    'fontWeight': 400,
                    'color': 'text.secondary',
                    'whiteSpace': 'nowrap',
                    'textDecoration': 'none',
                    'cursor': 'pointer',
                  }}
                >
                  <Translate
                    expression='tasks.AllTasks.TaskItem.MoreClients'
                    substitute={{count: hiddenClientConnections.length}}
                  />
                </Typography>
              </StickyTooltip>
            }
          </Stack>
        }
      </Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-end"
        sx={{
          position: 'relative',
          flexShrink: 0,
          textAlign: 'right',
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        <Divider
          orientation='vertical'
          flexItem
        />
        <Box
          ref={rightEdgeAnchorRef}
          sx={{
            position: 'absolute',
            right: -100,
            top: -7.5,
            width: 0,
            height: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: -120,
            top: 0,
            width: 120,
            height: '100%',
          }}
        />
        <Stack
          alignItems="flex-end"
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            component='button'
            ref={dueDateButtonRef}
            onClick={handleDueDateClick}
            sx={{
              border: 'none',
              background: 'none',
              p: 0,
              cursor: isStatusComplete ? 'default' : 'pointer',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: getDueDateColour(isStatusComplete, dueDateMoment),
                whiteSpace: 'nowrap',
              }}
            >
              {isToday ? dueDateMoment.format('h:mmA') : dueDateMoment.format('MMM D')}
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <DatePickerQuickOptionCalendar
        open={isDueDatePickerOpen}
        anchorRef={dueDateButtonRef}
        onClose={closeDueDatePicker}
        onChange={handleDueDateChange}
        value={task.dueDate}
      />
      {
        command.response &&
          <Box
            sx={{
              gridColumn: '1 / -1',
              pt: 1,
              pb: 1.5,
            }}
          >
            <CommandResponseView
              communicationTarget={command.communicationTarget}
              requester={requester}
              response={command.response}
              onApproveAndSendSuccess={handleApproveAndSendSuccess}
              onDiscard={command.discard}
              isRegenerating={command.isExecutingCommand}
              onRegenerate={command.regenerate}
            />
          </Box>
      }
      {
        isHoverToolboxEnabled &&
        <HoverToolBox
          toolBar={toolbarVisible}
          anchorEl={anchorEl}
          onClickEdit={() => onClickEditTask(task)}
          onClickDelete={() => removeTask(task)}
          hideTool={{
            emoji: true,
            copy: true,
            edit: false,
            delete: false,
            reply: true,
          }}
        />
      }
      <Divider sx={{gridColumn: '1 / -1'}} />
    </Box>
  );
};

TaskItem.propTypes = {
  task: PropTypes.shape({
    taskRn: PropTypes.string.isRequired,
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    ownerRn: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    ownerRns: PropTypes.arrayOf(PropTypes.string),
    connections: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        connectionRn: PropTypes.string,
      }),
    ])),
    connectionInfo: PropTypes.arrayOf(PropTypes.shape({
      connectionRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })),
    ownerInfo: PropTypes.arrayOf(PropTypes.shape({
      userRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
    })),
    dueDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    engagementRuleRn: PropTypes.string,
    playbookEngagementPlan: PropTypes.object,
    clientRn: PropTypes.string.isRequired,
    skillRn: PropTypes.string,
  }).isRequired,

  onClickEditTask: PropTypes.func,
  onUpdateDueDate: PropTypes.func,
  removeTask: PropTypes.func,
  onCompleteTask: PropTypes.func,
  isHoverToolboxEnabled: PropTypes.bool,
  isDueDatePickerEnabled: PropTypes.bool,
  requester: PropTypes.object.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskItem));
